/**
 * AuraFlow data layer.
 *
 * Every write that costs tokens or changes entitlement goes through an RPC;
 * the tables themselves are read-only to the client (see the RLS migration).
 * If you find yourself reaching for `supabase.from(...).insert(...)` here,
 * that logic belongs in a database function instead.
 */
import { supabase, isNative } from './supabase.js';

const unwrap = ({ data, error }) => {
  if (error) throw new ApiError(error);
  return data;
};

export class ApiError extends Error {
  constructor(error) {
    super(error.message ?? 'Request failed');
    this.name = 'ApiError';
    this.code = error.code;
    this.details = error.details;
  }
  /** Postgres RAISE codes we map to user-facing states. */
  get isInsufficientTokens() { return /insufficient tokens/i.test(this.message); }
  get isTooFar()             { return /too far from field/i.test(this.message); }
  get isCrossing()           { return /crosses an existing/i.test(this.message); }
  get isDeviceLimit()        { return /device limit reached/i.test(this.message); }
  get isNoSwitches()         { return /no device switches/i.test(this.message); }
  get isForbidden()          { return this.code === '42501'; }
}

// ------------------------------------------------------------------ auth
export const auth = {
  async session() {
    return (await supabase.auth.getSession()).data.session;
  },
  async user() {
    return (await supabase.auth.getUser()).data.user;
  },
  onChange(cb) {
    return supabase.auth.onAuthStateChange((_e, session) => cb(session)).data.subscription;
  },
  async signInWithPassword(email, password) {
    return unwrap(await supabase.auth.signInWithPassword({ email, password }));
  },
  /**
   * Resolves to { user, session }. session is null when the project requires
   * email confirmation — the caller shows the "check your inbox" state.
   */
  async signUp(email, password, fullName) {
    const options = { data: { full_name: fullName } };
    if (!isNative()) options.emailRedirectTo = window.location.origin;
    return unwrap(await supabase.auth.signUp({ email, password, options }));
  },
  async resendConfirmation(email) {
    return unwrap(await supabase.auth.resend({ type: 'signup', email }));
  },
  signOut: () => supabase.auth.signOut(),
};

// ------------------------------------------------------------------ profile
export const profile = {
  async get() {
    const user = await auth.user();
    if (!user) return null;
    return unwrap(await supabase.from('profile_v').select('*').eq('id', user.id).single());
  },
  /** Only full_name is writable; the guard trigger reverts anything else. */
  async rename(fullName) {
    const user = await auth.user();
    return unwrap(
      await supabase.from('profiles').update({ full_name: fullName }).eq('id', user.id).select().single()
    );
  },
};

// ------------------------------------------------------------------ subscription
export const subscription = {
  async current() {
    const rows = unwrap(await supabase.from('subscriptions').select('*').limit(1));
    return rows[0] ?? null;
  },
  isActive: (sub) =>
    Boolean(sub && sub.status === 'active' && new Date(sub.current_period_end) > new Date()),
};

// ------------------------------------------------------------------ devices
export const devices = {
  list: async () =>
    unwrap(await supabase.from('devices').select('*').order('created_at', { ascending: true })),
  register: async ({ deviceKey, label, kind, platform, userAgent }) =>
    unwrap(await supabase.rpc('register_device', {
      p_device_key: deviceKey, p_label: label, p_kind: kind,
      p_platform: platform, p_user_agent: userAgent,
    })),
  /** Opt a device in/out of field alerts. Resolves to the geofence token (or null). */
  setFieldAlerts: async (deviceId, enabled) =>
    unwrap(await supabase.rpc('set_field_alerts', { p_device_id: deviceId, p_enabled: enabled })),
  activate: async (deviceId, deactivateId = null) =>
    unwrap(await supabase.rpc('activate_device', {
      p_device_id: deviceId, p_deactivate_id: deactivateId,
    })),
  /** True if this device was remotely deactivated and must sign out. */
  consumeForcedLogout: async (deviceId) =>
    unwrap(await supabase.rpc('consume_force_logout', { p_device_id: deviceId })),
  deactivate: async (deviceId) =>
    unwrap(await supabase.rpc('deactivate_device', { p_device_id: deviceId })),
  remove: async (deviceId) =>
    unwrap(await supabase.from('devices').delete().eq('id', deviceId)),
  registerPushToken: async (deviceId, token) =>
    unwrap(await supabase.rpc('register_push_token', { p_device_id: deviceId, p_token: token })),
  clearPushToken: async (deviceId) =>
    unwrap(await supabase.rpc('clear_push_token', { p_device_id: deviceId })),
  rename: async (deviceId, label) =>
    unwrap(await supabase.from('devices').update({ label }).eq('id', deviceId).select().single()),
};

// ------------------------------------------------------------------ energy fields
export const fields = {
  /** Fields near a point, with server-computed distance and status. */
  nearby: async ({ lat, lng, withinMeters = 2000 }) =>
    unwrap(await supabase.rpc('nearby_fields', {
      p_lat: lat, p_lng: lng, p_within_m: withinMeters,
    })),
  mine: async () =>
    unwrap(await supabase.from('energy_fields_v').select('*').order('created_at', { ascending: false })),
  /** Cost is computed server-side; passing one would be ignored. */
  create: async ({ lat, lng, radiusMeters, intensity, isPublic = false, address = null }) =>
    unwrap(await supabase.rpc('create_energy_field', {
      p_lat: lat, p_lng: lng, p_radius_m: radiusMeters,
      p_intensity: intensity, p_is_public: isPublic, p_address: address,
    })),
  /** Cancel a field: own private ones, or any as an admin. No refund. */
  remove: async (fieldId) => unwrap(await supabase.rpc('remove_field', { p_field_id: fieldId })),
  reenergize: async (fieldId) =>
    unwrap(await supabase.rpc('reenergize_field', { p_field_id: fieldId })),
  checkIn: async ({ deviceId, fieldId, lat, lng }) =>
    unwrap(await supabase.rpc('check_into_field', {
      p_device_id: deviceId, p_field_id: fieldId, p_lat: lat, p_lng: lng,
    })),
  checkOut: async (deviceId) =>
    unwrap(await supabase.rpc('check_out_of_field', { p_device_id: deviceId })),
};

// ------------------------------------------------------------------ ceremonies
export const ceremonies = {
  list: async () =>
    unwrap(await supabase.from('ceremonies').select('*').order('held_on', { ascending: false })),
  create: async (row) => unwrap(await supabase.from('ceremonies').insert(row).select().single()),
  update: async (id, patch) =>
    unwrap(await supabase.from('ceremonies').update(patch).eq('id', id).select().single()),
  remove: async (id) => unwrap(await supabase.from('ceremonies').delete().eq('id', id)),
};

// ------------------------------------------------------------------ tokens
export const tokens = {
  packages: async () =>
    unwrap(await supabase.from('token_packages').select('*').eq('is_active', true).order('sort_order')),
  history: async (limit = 50) =>
    unwrap(await supabase.from('token_ledger').select('*').order('created_at', { ascending: false }).limit(limit)),
};

// ------------------------------------------------------------------ crystal
export const crystal = {
  /** XP and its ingredients, derived server-side. Level is computed in lib/crystal.js. */
  state: async () => unwrap(await supabase.rpc('crystal_state')),
  /** Once per calendar day; a repeat call is a no-op that returns the same state. */
  attune: async () => unwrap(await supabase.rpc('attune_crystal')),
};

// ------------------------------------------------------------------ keeper (admin)
// Every call is gated server-side on profiles.role; these only render for admins.
export const admin = {
  users: async () => unwrap(await supabase.rpc('admin_list_users')),
  grantTokens: async (userId, amount, note = null) =>
    unwrap(await supabase.rpc('admin_grant_tokens', { p_user_id: userId, p_amount: amount, p_note: note })),
  grantSubscription: async (userId, tier, days = 30, note = null) =>
    unwrap(await supabase.rpc('admin_grant_subscription', { p_user_id: userId, p_tier: tier, p_days: days, p_note: note })),
  endSubscription: async (userId) =>
    unwrap(await supabase.rpc('admin_end_subscription', { p_user_id: userId })),
  affiliateOf: async (userId) => unwrap(await supabase.rpc('admin_affiliate_of', { p_user_id: userId })),
  /** Run (or preview) the creator payout round. */
  runPayouts: async ({ dryRun = true, minimumCents = 1000 } = {}) => {
    const { data, error } = await supabase.functions.invoke('affiliate-payout', { body: { dry_run: dryRun, minimum_cents: minimumCents } });
    if (error) throw new ApiError({ message: error.message });
    if (!data?.ok) throw new ApiError({ message: data?.error ?? 'Payout run failed' });
    return data;
  },
  setAffiliate: async (userId, { enabled = true, commissionPct = null, welcomeTokens = null, code = null, note = null } = {}) =>
    unwrap(await supabase.rpc('admin_set_affiliate', {
      p_user_id: userId, p_enabled: enabled, p_commission_pct: commissionPct,
      p_welcome_tokens: welcomeTokens, p_code: code, p_note: note,
    })),
};

// ------------------------------------------------------------------ affiliates
export const REF_KEY = 'auraflow.ref';   // invite code waiting to be claimed by a new account
export const affiliate = {
  overview: async () => unwrap(await supabase.rpc('affiliate_overview')),
  claim: async (code) => unwrap(await supabase.rpc('claim_referral', { p_code: code })),
  /** Remember a code seen before sign-up (link or typed); claimed on the first session. */
  remember: (code) => { const c = String(code ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase(); if (c) localStorage.setItem(REF_KEY, c); },
  pending: () => localStorage.getItem(REF_KEY),
  /** Stripe Connect onboarding / status / dashboard, via the affiliate-connect function. */
  connect: async (action = 'link') => {
    const { data, error } = await supabase.functions.invoke('affiliate-connect', {
      body: { action, return_url: window.location.origin.startsWith('http') && !window.location.origin.includes('localhost') ? window.location.origin : undefined },
    });
    if (error) throw new ApiError({ message: error.message });
    if (!data?.ok) throw new ApiError({ message: data?.error ?? 'Payout setup unavailable' });
    return data;
  },
  forget: () => localStorage.removeItem(REF_KEY),
};

// ------------------------------------------------------------------ notifications
export const notifications = {
  list: async () =>
    unwrap(await supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(50)),
  markRead: async (id) =>
    unwrap(await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id)),
};
